import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactJoyride, { STATUS } from 'react-joyride';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  X,
  Clock,
  MapPin,
  FileText,
  BookOpen,
  Settings,
  Star,
  Users,
  ArrowUpRight,
  HelpCircle,
  Loader2,
} from 'lucide-react';

import AudioPlayer from './components/AudioPlayer';
import ArchiveList from './components/ArchiveList';
import QueueCarousel from './components/QueueCarousel';
import SanghamittaModal from './components/SanghamittaModal';
import Toast from './components/Toast';
import { getTutorialSteps } from './tutorialSteps';
import {
  DEFAULT_DATA_URL,
  processData,
  getHistoryMeta,
  getBlockStart,
  generateRadioSchedule,
  HISTORY_KEY,
  QUEUE_KEY,
  FAV_KEY,
  BOOKMARK_KEY,
} from './utils/audioUtils';
import useLocalStorage from './hooks/useLocalStorage';
import useShakaPlayer from './hooks/useShakaPlayer';
import useMediaSession from './hooks/useMediaSession';
import useAudioAnalytics from './hooks/useAudioAnalytics';
import usePartySession from './hooks/usePartySession';

const MOCK_DATA_RAW = [
  {
    speaker: 'Sujataa',
    dest: 'https://baws.in/books/baws/HI/Volume_40/pdf/20',
    title:
      'जो लोग करोड़ों लोगों को अछूत और अपराधी मानते हां उन्हें आजादी मांगने का हक नहीं - सोलापुर',
    description:
      'This speech addresses the issues of freedom and rights for the marginalized. \n\nRead more at https://baws.in/books/baws/HI/Volume_40 \n\nIt emphasizes that true freedom cannot exist where discrimination persists.',
    number: 233,
    date: '14-1-1946',
    place: 'सोलापुर',
    thumbnail:
      'https://baws-fd-endpoint-media-11cd090b-akdpeve3a7gef8gg.z03.azurefd.net/media/baws-audio/Sujataa/HI/Volume_40/thumbs/233.png',
    duration: '05:04',
    stream:
      'https://baws-fd-endpoint-media-11cd090b-akdpeve3a7gef8gg.z03.azurefd.net/media/baws-audio/Sujataa/HI/Volume_40/stream/233/stream.mpd',
    subtitles:
      'https://baws-fd-endpoint-media-11cd090b-akdpeve3a7gef8gg.z03.azurefd.net/media/baws-audio/Sujataa/HI/Volume_40/sbtl/233.srt',
    createdAt: '2023-01-01T12:00:00Z',
    chapters: [],
  },
  {
    speaker: 'Sujataa',
    dest: 'https://baws.in/books/baws/HI/Volume_40/pdf/22',
    title: 'क्षमतावान न भी हों तो चलेगा, लोग निष्ठावान होने चाहिएं - सोलापुर',
    description:
      'A powerful discourse on loyalty vs ability. Visit our archives: https://baws.in/ \n\nThe speaker argues that dedication to the cause is paramount.',
    number: 234,
    date: '14-1-1946',
    place: 'सोलापुर',
    thumbnail:
      'https://baws-fd-endpoint-media-11cd090b-akdpeve3a7gef8gg.z03.azurefd.net/media/baws-audio/Sujataa/HI/Volume_40/thumbs/234.png',
    duration: '13:03',
    stream:
      'https://baws-fd-endpoint-media-11cd090b-akdpeve3a7gef8gg.z03.azurefd.net/media/baws-audio/Sujataa/HI/Volume_40/stream/234/stream.mpd',
    subtitles:
      'https://baws-fd-endpoint-media-11cd090b-akdpeve3a7gef8gg.z03.azurefd.net/media/baws-audio/Sujataa/HI/Volume_40/sbtl/234.srt',
    createdAt: '2023-01-01T12:00:00Z',
    chapters: [],
  },
  {
    speaker: 'Sujataa',
    dest: 'https://baws.in/books/baws/HI/Volume_40/pdf/27',
    title:
      'स्वराज्य के लिए मेरा कतई विरोध नहीं, परंतु स्वराज्य में हमें भी हिस्सेदारी मिलनी चाहिए - दहिवडी (सातारा)',
    description:
      'On the topic of Swaraj and representation. \n\nKey points discussed:\n1. Representation\n2. Rights\n3. Governance',
    number: 235,
    date: '15-2-1946',
    place: 'दahivadi (Satara)',
    thumbnail:
      'https://baws-fd-endpoint-media-11cd090b-akdpeve3a7gef8gg.z03.azurefd.net/media/baws-audio/Sujataa/HI/Volume_40/thumbs/235.png',
    duration: '11:05',
    stream:
      'https://baws-fd-endpoint-media-11cd090b-akdpeve3a7gef8gg.z03.azurefd.net/media/baws-audio/Sujataa/HI/Volume_40/stream/235/stream.mpd',
    subtitles:
      'https://baws-fd-endpoint-media-11cd090b-akdpeve3a7gef8gg.z03.azurefd.net/media/baws-audio/Sujataa/HI/Volume_40/sbtl/235.srt',
    createdAt: '2023-01-01T12:00:00Z',
    chapters: [],
  },
];

const EMPTY_ARRAY = [];
const LOADING_ITEM = { id: 'loading', title: 'Loading...', thumbnail: '' };

export default function AudioPlayerPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isFullPage = location.pathname === '/listen';
  const isBookPage = location.pathname.startsWith('/books/');
  const [targetNode, setTargetNode] = useState(null);

  // Sync portal target
  useEffect(() => {
    if (isFullPage) {
      const timer = setTimeout(() => {
        const el = document.getElementById('audio-player-target');
        setTargetNode(el);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setTargetNode(null);
    }
  }, [isFullPage, location.pathname]);

  const [items, setItems] = useState([]);
  const [radioMuted, setRadioMuted] = useState(false);
  const [manualQueue, setManualQueue] = useLocalStorage(QUEUE_KEY, []);
  const [currentManualItem, setCurrentManualItem] = useState(null);
  const [mode, setMode] = useState('ondemand');
  const isRadioMode = mode === 'radio';
  const [currentRadioItem, setCurrentRadioItem] = useState(null);
  const [shouldAutoPlay, setShouldAutoPlay] = useState(false);
  const [view, setView] = useState('player');
  const [favorites, setFavorites] = useLocalStorage(FAV_KEY, []);
  const [bookmarks, setBookmarks] = useLocalStorage(BOOKMARK_KEY, {});
  const [partyItem, setPartyItem] = useState(null);
  const [partySession, setPartySession] = useState(null);
  const [history, setHistory] = useLocalStorage(HISTORY_KEY, {});

  const [blockStartMs, setBlockStartMs] = useState(getBlockStart());

  const [videoElement, setVideoElement] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false);

  // Tutorial State
  const [runTutorial, setRunTutorial] = useState(false);
  const [tutorialLang, setTutorialLang] = useState('en');
  const [tutorialSteps, setTutorialSteps] = useState([]);
  const [showLangModal, setShowLangModal] = useState(false);

  useEffect(() => {
    if (isFullPage) {
      const seen = localStorage.getItem('baws-audio-tutorial-seen');
      if (!seen) {
        setShowLangModal(true);
      }
    }
  }, [isFullPage]);

  useEffect(() => {
    if (showLangModal && isFullPage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [showLangModal, isFullPage]);

  useEffect(() => {
    setTutorialSteps(getTutorialSteps(tutorialLang));
  }, [tutorialLang]);

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTutorial(false);
      localStorage.setItem('baws-audio-tutorial-seen', 'true');
    }
  };

  const startTutorial = () => {
    setShowLangModal(true);
  };

  const handleLanguageSelect = (lang) => {
    setTutorialLang(lang);
    setShowLangModal(false);
    setRunTutorial(true);
  };

  const handleSkipTutorial = () => {
    setShowLangModal(false);
    localStorage.setItem('baws-audio-tutorial-seen', 'true');
  };

  const [notification, setNotification] = useState(null);

  const showToast = useCallback((msg) => {
    setNotification(msg);
  }, []);

  const toggleFavorite = useCallback(
    (id) => {
      setFavorites((prev) => {
        const next = prev.includes(id)
          ? prev.filter((i) => i !== id)
          : [...prev, id];
        return next;
      });
    },
    [setFavorites]
  );

  const handleAddBookmark = useCallback(
    (id, time) => {
      setBookmarks((prev) => {
        const current = Array.isArray(prev[id])
          ? prev[id]
          : prev[id] !== undefined
            ? [prev[id]]
            : [];
        const nextList = [...current, time].sort((a, b) => a - b);
        const nextState = { ...prev, [id]: nextList };
        return nextState;
      });
    },
    [setBookmarks]
  );

  const handleRemoveBookmark = useCallback(
    (id, timeToRemove) => {
      setBookmarks((prev) => {
        const current = Array.isArray(prev[id]) ? prev[id] : [];
        const nextList = current.filter(
          (t) => Math.abs(t - timeToRemove) > 0.01
        );
        const nextState = { ...prev, [id]: nextList };
        return nextState;
      });
    },
    [setBookmarks]
  );

  const saveHistory = useCallback(
    (key, time) => {
      setHistory((prev) => {
        const next = {
          ...prev,
          [key]: { progress: time, lastPlayed: Date.now() },
        };
        return next;
      });
    },
    [setHistory]
  );

  const handleManualEnded = useCallback(() => {
    const idx = manualQueue.findIndex((i) => i.id === currentManualItem?.id);
    let nextItem = null;
    if (idx >= 0 && idx < manualQueue.length - 1)
      nextItem = manualQueue[idx + 1];
    else if (manualQueue.length) nextItem = manualQueue[0];

    if (nextItem) {
      setShouldAutoPlay(true);
      setCurrentManualItem({
        id: `countdown-manual-${Date.now()}`,
        type: 'countdown',
        title: `Up Next: ${nextItem.title}`,
        thumbnail: nextItem.thumbnail,
        duration: '00:10',
        nextItemTitle: nextItem.title,
        actualNextItem: nextItem,
        preloadSrc: nextItem.stream || nextItem.audio,
        preloadSubtitles: nextItem.subtitles,
      });
    }
  }, [currentManualItem, manualQueue]);

  const advanceManualTrack = useCallback(() => {
    let nextItem = null;
    if (currentManualItem?.actualNextItem) {
      nextItem = currentManualItem.actualNextItem;
    } else {
      const idx = manualQueue.findIndex((i) => i.id === currentManualItem?.id);
      if (manualQueue.length > 0) {
        nextItem = manualQueue[(idx + 1) % manualQueue.length];
      }
    }

    if (nextItem) {
      setCurrentManualItem({ ...nextItem, ignoreHistory: true });
    }
    setShouldAutoPlay(true);
  }, [currentManualItem, manualQueue]);

  useEffect(() => {
    const interval = setInterval(() => {
      const newBlockStart = getBlockStart();
      if (newBlockStart !== blockStartMs) setBlockStartMs(newBlockStart);
    }, 60000);
    return () => clearInterval(interval);
  }, [blockStartMs]);

  const radioState = useMemo(
    () => generateRadioSchedule(items, blockStartMs),
    [items, blockStartMs]
  );

  const handleReorder = useCallback(
    (id, direction) => {
      setManualQueue((prev) => {
        const index = prev.findIndex((i) => i.id === id);
        if (index < 0) return prev;
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= prev.length) return prev;

        const newQueue = [...prev];
        const [item] = newQueue.splice(index, 1);
        newQueue.splice(newIndex, 0, item);
        return newQueue;
      });
    },
    [setManualQueue]
  );

  // Safety Effect for Initial Track
  useEffect(() => {
    if (items.length > 0 && !currentManualItem && !isRadioMode) {
      let mostRecentItem = null;
      let maxTimestamp = 0;
      items.forEach((item) => {
        const meta = getHistoryMeta(history, item.id);
        if (meta.lastPlayed > maxTimestamp) {
          maxTimestamp = meta.lastPlayed;
          mostRecentItem = item;
        }
      });
      setCurrentManualItem(mostRecentItem || items[0]);
    }
  }, [items, currentManualItem, isRadioMode, history]);

  // URL State Synchronization
  useEffect(() => {
    if (items.length === 0 || !isFullPage) return;

    const params = new URLSearchParams(location.search);
    let updated = false;

    if (isRadioMode) {
      if (params.get('radio') !== 'true') {
        params.set('radio', 'true');
        params.delete('item');
        params.delete('t');
        params.delete('party');
        updated = true;
      }
    } else {
      if (params.has('radio')) {
        params.delete('radio');
        updated = true;
      }
      if (currentManualItem && currentManualItem.id) {
        if (params.get('item') !== currentManualItem.id) {
          params.set('item', currentManualItem.id);
          params.delete('t');
          updated = true;
        }
      }
      if (partySession) {
        if (params.get('party') !== String(partySession.startTime)) {
          params.set('party', String(partySession.startTime));
          updated = true;
        }
      } else if (params.has('party')) {
        params.delete('party');
        updated = true;
      }
    }

    if (updated) {
      navigate(`?${params.toString()}`, { replace: true });
    }
  }, [
    isRadioMode,
    currentManualItem?.id,
    partySession,
    items.length,
    isFullPage,
    location.search,
    navigate,
  ]);

  // Initial Data Fetch and Deep Link Processing
  useEffect(() => {
    const init = async () => {
      const params = new URLSearchParams(location.search);
      const sharedId = params.get('item');
      const sharedTime = params.get('t');
      const partyTime = params.get('party');
      const radioParam = params.get('radio');
      const autoplayParam = params.get('autoplay');
      const dataUrl = params.get('data') || DEFAULT_DATA_URL;

      let processedItems = items;
      if (items.length === 0) {
        try {
          const res = await fetch(dataUrl, { mode: 'cors' });
          if (!res.ok) throw new Error('Network response was not ok');
          const rawData = await res.json();
          if (Array.isArray(rawData)) {
            processedItems = processData(rawData);
            setItems(processedItems);
          }
        } catch (e) {
          console.warn('Failed to fetch external data, trying local copy:', e);
          try {
            const localRes = await fetch('/audio_data.json');
            if (!localRes.ok) throw new Error('Local file not found');
            const localData = await localRes.json();
            if (Array.isArray(localData)) {
              processedItems = processData(localData);
              setItems(processedItems);
            }
          } catch (localErr) {
            console.warn(
              'Failed to fetch local data, falling back to mock:',
              localErr
            );
            if (MOCK_DATA_RAW.length > 0) {
              processedItems = processData(MOCK_DATA_RAW);
              setItems(processedItems);
            }
          }
        }
      }

      if (radioParam === 'true') {
        setShouldAutoPlay(autoplayParam === 'true');
        setMode('radio');
      }

      if (sharedId) {
        const found = processedItems.find(
          (i) => i.id === decodeURIComponent(sharedId)
        );
        if (found) {
          setCurrentManualItem(found);
          if (partyTime) {
            setPartySession({
              itemId: found.id,
              startTime: parseInt(partyTime, 10),
            });
            setShouldAutoPlay(false);
          } else if (sharedTime) {
            setHistory((prev) => ({
              ...prev,
              [found.id]: {
                progress: Number(sharedTime),
                lastPlayed: Date.now(),
              },
            }));
            setShouldAutoPlay(false);
          }
        }
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, items.length === 0]);

  // Radio Schedule Updater
  useEffect(() => {
    if (!isRadioMode) return;
    const updateRadio = () => {
      const nowMs = Date.now();
      const offset = (nowMs - radioState.blockStartMs) / 1000;
      const activeItem = radioState.schedule.find(
        (i) => offset >= i.playStartOffset && offset < i.playEndOffset
      );
      if (activeItem && activeItem.uniqueKey !== currentRadioItem?.uniqueKey) {
        setCurrentRadioItem(activeItem);
      }
    };
    updateRadio();
    const interval = setInterval(updateRadio, 1000);
    return () => clearInterval(interval);
  }, [radioState, isRadioMode, currentRadioItem]);

  const handlePlay = useCallback((item) => {
    setMode('ondemand');
    setShouldAutoPlay(true);
    setCurrentManualItem({ ...item, ignoreHistory: false });
  }, []);

  const handleReturnToParty = useCallback(() => {
    if (!partySession) return;
    const item = items.find((i) => i.id === partySession.itemId);
    if (item) {
      setMode('ondemand');
      setShouldAutoPlay(false);
      setCurrentManualItem(item);
    }
  }, [items, partySession]);

  const handleQueue = useCallback(
    (item) => {
      setManualQueue((prev) => {
        if (!prev.find((i) => i.id === item.id)) return [...prev, item];
        return prev;
      });
    },
    [setManualQueue]
  );

  const removeFromQueue = useCallback(
    (id) => setManualQueue((prev) => prev.filter((i) => i.id !== id)),
    [setManualQueue]
  );

  const hasQueueItems = useMemo(() => {
    if (isRadioMode && radioState)
      return radioState.schedule.some((i) => i.type !== 'countdown');
    return manualQueue.length > 0;
  }, [isRadioMode, radioState, manualQueue.length]);

  // Fallback track for radio mode when no item is selected yet
  const fallbackRadioItem = useMemo(() => {
    if (items.length > 0) return { ...items[0], isLoading: true };
    return null;
  }, [items]);

  // Determine Current Active Item
  const activeItem = useMemo(() => {
    if (isRadioMode) {
      return currentRadioItem || fallbackRadioItem;
    }
    return currentManualItem;
  }, [isRadioMode, currentRadioItem, fallbackRadioItem, currentManualItem]);

  const currentItemForHooks = activeItem || LOADING_ITEM;
  const safeCurrentId = isRadioMode
    ? activeItem?.uniqueKey || activeItem?.id
    : activeItem?.id;
  const isCountdown = activeItem?.type === 'countdown';

  const handlePlaybackChange = useCallback((isP) => {
    setIsPlaying(isP);
    setShouldAutoPlay(isP);
    if (isP) setHasStartedPlaying(true);
  }, []);

  const exitRadio = useCallback(() => setMode('ondemand'), []);
  const startParty = useCallback((item) => setPartyItem(item), []);

  // --- MEDIA LOGIC HOOKS ---
  const { playerInstance, playerError, isSubtitlesOn, setIsSubtitlesOn } =
    useShakaPlayer({
      currentItem: currentItemForHooks,
      isRadioMode,
      radioState,
      isCountdown,
      radioMuted,
      setRadioMuted,
      onNext: advanceManualTrack,
      shouldAutoPlay,
      history,
      partySession,
      videoElement,
    });

  useMediaSession({
    currentItem: currentItemForHooks,
    isCountdown,
    isRadioMode,
    isPlaying,
    videoElement,
    onExitRadio: exitRadio,
    onNext: advanceManualTrack,
    setRadioMuted,
  });

  useAudioAnalytics(currentItemForHooks, isPlaying, isRadioMode);

  const partyCountdown = usePartySession(
    partySession,
    currentItemForHooks,
    isRadioMode,
    videoElement
  );

  const currentBookmarks = useMemo(() => {
    return (activeItem && bookmarks[activeItem.id]) || EMPTY_ARRAY;
  }, [activeItem?.id, bookmarks]);

  // Shared props for UI - MEMOIZED to prevent loops
  const sharedAudioProps = useMemo(
    () => ({
      currentItem: activeItem,
      isRadioMode,
      radioState,
      onNext: advanceManualTrack,
      onManualEnded: handleManualEnded,
      shouldAutoPlay,
      history,
      saveHistory,
      toggleFavorite,
      isFavorite: activeItem ? favorites.includes(activeItem.id) : false,
      onStartParty: startParty,
      partySession,
      savedBookmarks: currentBookmarks,
      onAddBookmark: handleAddBookmark,
      onRemoveBookmark: handleRemoveBookmark,
      radioMuted,
      setRadioMuted,
      onExitRadio: exitRadio,
      showToast,
      videoElement,
      playerInstance,
      playerError,
      isSubtitlesOn,
      setIsSubtitlesOn,
      partyCountdown,
      onPlaybackChange: handlePlaybackChange,
    }),
    [
      activeItem,
      isRadioMode,
      radioState,
      advanceManualTrack,
      handleManualEnded,
      shouldAutoPlay,
      history,
      saveHistory,
      favorites,
      startParty,
      partySession,
      currentBookmarks,
      handleAddBookmark,
      handleRemoveBookmark,
      radioMuted,
      setRadioMuted,
      exitRadio,
      showToast,
      videoElement,
      playerInstance,
      playerError,
      isSubtitlesOn,
      setIsSubtitlesOn,
      partyCountdown,
      handlePlaybackChange,
    ]
  );

  const fullPlayerContent = activeItem && (
    <div className="bg-slate-950 text-slate-200 font-sans selection:bg-blue-500/30 flex flex-col w-full min-h-full">
      <ReactJoyride
        steps={tutorialSteps}
        run={runTutorial}
        continuous
        showProgress
        showSkipButton
        disableOverlayClose={true}
        spotlightClicks={true}
        scrollOffset={150}
        floaterProps={{ disableAnimation: true }}
        styles={{
          options: {
            zIndex: 10000,
            primaryColor: '#3b82f6',
            textColor: '#e2e8f0',
            backgroundColor: '#0f172a',
            arrowColor: '#0f172a',
            overlayColor: 'rgba(0, 0, 0, 0.85)',
          },
          tooltip: { fontSize: '14px', borderRadius: '1rem' },
          buttonNext: {
            backgroundColor: '#3b82f6',
            color: '#fff',
            fontWeight: 'bold',
            outline: 'none',
          },
          buttonBack: { color: '#94a3b8', marginRight: 10 },
          buttonSkip: { color: '#64748b' },
        }}
        callback={handleJoyrideCallback}
        locale={{
          last: tutorialLang === 'hi' ? 'समाप्त (Finish)' : 'Finish',
          skip: tutorialLang === 'hi' ? 'छोड़ें (Skip)' : 'Skip',
          next: tutorialLang === 'hi' ? 'अगला (Next)' : 'Next',
          back: tutorialLang === 'hi' ? 'पीछे (Back)' : 'Back',
        }}
      />

      {showLangModal && (
        <div className="fixed inset-0 z-[10001] bg-black/80 backdrop-blur-sm overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-sm w-full relative shadow-2xl my-auto">
              <button
                onClick={handleSkipTutorial}
                className="absolute top-4 right-4 text-slate-400 hover:text-white"
                title="Skip Tutorial"
              >
                <X size={24} />
              </button>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white">
                  <HelpCircle size={32} />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Welcome!</h2>
                <p className="text-slate-400">
                  Select your preferred language for the tour.
                </p>
                <p className="text-slate-400 text-sm mt-1">
                  ट्यूटोरियल के लिए अपनी भाषा चुनें।
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => handleLanguageSelect('en')}
                  className="flex flex-col items-center justify-center gap-2 p-4 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-blue-500 rounded-xl transition-all"
                >
                  <span className="text-2xl">A</span>
                  <span className="font-bold text-white">English</span>
                </button>
                <button
                  onClick={() => handleLanguageSelect('hi')}
                  className="flex flex-col items-center justify-center gap-2 p-4 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-blue-500 rounded-xl transition-all"
                >
                  <span className="text-2xl">अ</span>
                  <span className="font-bold text-white">हिंदी</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toast message={notification} onClose={() => setNotification(null)} />

      {/* Header Controls */}
      <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-end gap-4 w-full bg-slate-950/80 backdrop-blur border-b border-slate-800/50 sticky top-0 z-40">
        <button
          onClick={startTutorial}
          className="text-slate-400 hover:text-white"
          title="Start Tutorial"
        >
          <HelpCircle size={20} />
        </button>
        <div className="tour-mode-switch flex items-center gap-2 bg-slate-950 border border-slate-800 rounded-full p-1">
          <button
            onClick={() => setMode('radio')}
            className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase ${mode === 'radio' ? 'bg-red-600 text-white' : 'text-slate-400'}`}
          >
            Radio
          </button>
          <button
            onClick={() => setMode('ondemand')}
            className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase ${mode === 'ondemand' ? 'bg-blue-600 text-white' : 'text-slate-400'}`}
          >
            On-demand
          </button>
        </div>
      </div>

      <div className="md:hidden flex border-b border-slate-800 bg-slate-900/50 flex-shrink-0">
        <button
          onClick={() => setView('player')}
          className={`flex-1 hidden py-3 text-xs font-bold uppercase tracking-wide ${view === 'player' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-slate-500'}`}
        >
          Player
        </button>
      </div>

      <main className="flex-grow w-full relative flex flex-col">
        {view === 'player' && (
          <div className="flex-shrink-0 w-full bg-slate-950 z-10 shadow-xl relative border-b border-slate-800/50">
            <div className="max-w-7xl mx-auto p-4 lg:p-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-6">
                <AudioPlayer {...sharedAudioProps} isMini={false} />
              </div>
              <div className="tour-queue hidden lg:block lg:col-span-6 relative">
                <div className="absolute inset-0 overflow-y-auto custom-scrollbar rounded-xl">
                  <QueueCarousel
                    queue={manualQueue}
                    currentId={safeCurrentId}
                    onPlay={handlePlay}
                    onRemove={removeFromQueue}
                    onReorder={handleReorder}
                    onClear={() => setManualQueue([])}
                    onSave={() => showToast('Saved!')}
                    isRadioMode={isRadioMode}
                    radioState={radioState}
                    history={history}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex-grow">
          {view === 'player' ? (
            <div className="max-w-7xl mx-auto p-0 md:p-6 flex flex-col gap-6 pb-24">
              {hasQueueItems && (
                <div className="lg:hidden p-4 pb-0">
                  <QueueCarousel
                    queue={manualQueue}
                    currentId={safeCurrentId}
                    onPlay={handlePlay}
                    onRemove={removeFromQueue}
                    onReorder={handleReorder}
                    onClear={() => setManualQueue([])}
                    onSave={() => showToast('Saved!')}
                    isRadioMode={isRadioMode}
                    radioState={radioState}
                    history={history}
                  />
                </div>
              )}
              {mode === 'ondemand' && (
                <div className="tour-archive-list">
                  <ArchiveList
                    items={items}
                    onPlay={handlePlay}
                    onQueue={handleQueue}
                    onRemove={removeFromQueue}
                    queue={manualQueue}
                    history={history}
                    toggleFavorite={toggleFavorite}
                    favorites={favorites}
                    onStartParty={(item) => setPartyItem(item)}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="h-full overflow-y-auto">
              {view === 'library' && (
                <div className="flex flex-col items-center justify-center h-96 text-slate-500">
                  <BookOpen size={48} className="mb-4 opacity-50" />
                  <h2 className="text-xl font-bold text-slate-300">
                    Full Library
                  </h2>
                  <p className="mt-2">
                    Browse collections, volumes, and speeches.
                  </p>
                </div>
              )}
              {view === 'settings' && (
                <div className="flex flex-col items-center justify-center h-96 text-slate-500">
                  <Settings size={48} className="mb-4 opacity-50" />
                  <h2 className="text-xl font-bold text-slate-300">Settings</h2>
                  <p className="mt-2">
                    Audio quality, themes, and account preferences.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <SanghamittaModal
          isOpen={!!partyItem}
          onClose={() => setPartyItem(null)}
          item={partyItem}
          showToast={showToast}
        />
        {partySession &&
          partySession.itemId !== activeItem?.id &&
          !isRadioMode && (
            <div className="fixed bottom-24 right-4 z-50 animate-bounce">
              <button
                onClick={handleReturnToParty}
                className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-full font-bold shadow-lg flex items-center gap-2 transition-transform hover:scale-105"
              >
                <Users size={20} /> Return to Party <ArrowUpRight size={18} />
              </button>
            </div>
          )}
      </main>
    </div>
  );

  // Return Loading Screen if items not yet loaded
  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-200 font-sans flex items-center justify-center flex-col gap-4">
        <Loader2 size={48} className="text-blue-500 animate-spin" />
        <h2 className="text-xl font-bold">Loading Library...</h2>
        <p className="text-slate-500 text-sm">Connecting to data source...</p>
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '1px',
          height: '1px',
          opacity: 0,
          zIndex: -1,
          overflow: 'hidden',
          pointerEvents: 'none',
        }}
      >
        <video
          ref={setVideoElement}
          poster={activeItem?.thumbnail}
          crossOrigin="anonymous"
          playsInline
        >
          {activeItem?.subtitles && (
            <track
              key={activeItem.id}
              label="Hindi"
              kind="subtitles"
              srcLang="hi"
              src={activeItem.subtitles}
              default={isSubtitlesOn}
            />
          )}
        </video>
      </div>

      {targetNode && ReactDOM.createPortal(fullPlayerContent, targetNode)}

      {!isFullPage &&
        hasStartedPlaying &&
        !(isMobile && isBookPage && (!isPlaying || radioMuted)) && (
          <AudioPlayer
            {...sharedAudioProps}
            isMini={true}
            onClick={() => navigate('/listen')}
            onExpand={() => navigate('/listen')}
          />
        )}
    </>
  );
}
